import React, { useMemo } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { LoadingSpinner } from '../common/LoadingSpinner';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useTheme, ThemeColors } from '../../context/ThemeContext';
import { useReportCardList, useReportCardPdf } from '../../hooks/useReportCards';
import {
  formatMarksOutOf,
  formatReportCardDate,
  formatReportCardPeriod,
  gradeBadgeColors,
  reportCardFileName,
} from '../../utils/reportCards';
import type { ReportCardListItem } from '../../types/reportCard';

interface Props {
  /** The enrolment whose published report cards are listed. */
  enrollmentId: string | null | undefined;
  /** Used for the downloaded file's name. */
  studentName?: string;
  /** Bumped by the host screen's pull-to-refresh. */
  refreshKey?: number;
  onOpen: (card: ReportCardListItem) => void;
  notify: (message: string, type: 'success' | 'error') => void;
}

/**
 * "Report cards" on the Exam Results screen (native and web): the child's
 * PUBLISHED report cards, newest first. Tap a card for the in-app view; the
 * PDF is the server's own.
 */
export function ReportCardsSection({ enrollmentId, studentName, refreshKey = 0, onOpen, notify }: Props) {
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { cards, loading, error, reload } = useReportCardList(enrollmentId, refreshKey);
  const { download, busyId } = useReportCardPdf(notify);

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle} accessibilityRole="header">Report cards</Text>

      {loading ? (
        <View style={styles.stateBox}>
          <LoadingSpinner size="small" message="Loading report cards..." />
        </View>
      ) : error ? (
        <View style={styles.stateBox}>
          <FeatherIcon name="alert-circle" size={20} color={colors.danger} />
          <Text style={styles.stateText}>{error}</Text>
          <TouchableOpacity onPress={reload} accessibilityRole="button" style={styles.retryButton}>
            <Text style={styles.retryText}>Try again</Text>
          </TouchableOpacity>
        </View>
      ) : cards.length === 0 ? (
        <View style={styles.stateBox}>
          <FeatherIcon name="award" size={20} color={colors.textMuted} />
          <Text style={styles.stateText}>No report cards published yet.</Text>
        </View>
      ) : (
        cards.map(card => {
          const grade = gradeBadgeColors(card.overallGrade);
          const saving = busyId === `save:${card.id}`;
          const published = formatReportCardDate(card.publishedDate);
          return (
            <TouchableOpacity
              key={card.id}
              style={styles.card}
              activeOpacity={0.8}
              onPress={() => onOpen(card)}
              accessibilityRole="button"
              accessibilityLabel={`Open report card ${card.configName}`}>
              <View style={styles.cardTop}>
                <View style={styles.cardIcon}>
                  <FeatherIcon name="award" size={20} color={isDark ? colors.primary : '#217BD6'} />
                </View>
                <View style={styles.cardInfo}>
                  <Text style={styles.cardTitle} numberOfLines={2}>{card.configName}</Text>
                  <Text style={styles.cardMeta}>
                    {[formatReportCardPeriod(card.startDate, card.endDate), card.className].filter(Boolean).join(' · ')}
                  </Text>
                </View>
                <FeatherIcon name="chevron-right" size={20} color={colors.textMuted} />
              </View>

              <View style={styles.cardStats}>
                <View style={[styles.statBlock, { flex: 1.4 }]}>
                  <Text style={styles.statLabel}>Total</Text>
                  <Text style={styles.statValue}>{formatMarksOutOf(card.totalMarks, card.totalMaxMarks)}</Text>
                </View>
                <View style={styles.statBlock}>
                  <Text style={styles.statLabel}>Grade</Text>
                  {card.overallGrade ? (
                    <View style={[styles.gradeBadge, { backgroundColor: grade.bg }]}>
                      <Text style={[styles.gradeBadgeText, { color: grade.text }]}>{card.overallGrade}</Text>
                    </View>
                  ) : (
                    <Text style={styles.statValue}>—</Text>
                  )}
                </View>
                <View style={[styles.statBlock, { flex: 1.4 }]}>
                  <Text style={styles.statLabel}>Published</Text>
                  <Text style={styles.statValueSmall}>{published || '—'}</Text>
                </View>
              </View>

              <TouchableOpacity
                style={[styles.downloadButton, saving && { opacity: 0.7 }]}
                onPress={() => download(card.id, reportCardFileName(card.configName, studentName))}
                disabled={!!busyId}
                accessibilityRole="button"
                accessibilityLabel={`Download PDF of ${card.configName}`}>
                {saving ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <>
                    <FeatherIcon name="download" size={16} color="#FFFFFF" style={{ marginRight: 8 }} />
                    <Text style={styles.downloadButtonText}>Download PDF</Text>
                  </>
                )}
              </TouchableOpacity>
            </TouchableOpacity>
          );
        })
      )}
    </View>
  );
}

const createStyles = (colors: ThemeColors, isDark: boolean) =>
  StyleSheet.create({
    section: { marginBottom: 24 },
    sectionTitle: { fontSize: 18, fontWeight: '700', color: colors.textPrimary, marginBottom: 12 },
    stateBox: {
      alignItems: 'center',
      paddingVertical: 24,
      paddingHorizontal: 16,
      backgroundColor: colors.card,
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border,
    },
    stateText: { marginTop: 8, fontSize: 14, color: colors.textSecondary, textAlign: 'center', lineHeight: 20 },
    retryButton: { marginTop: 8, paddingVertical: 4, paddingHorizontal: 8 },
    retryText: { fontSize: 14, fontWeight: '700', color: colors.primary },
    card: {
      backgroundColor: colors.card,
      borderRadius: 16,
      padding: 16,
      borderWidth: 1,
      borderColor: colors.border,
      marginBottom: 12,
      elevation: 2,
      shadowColor: colors.shadow,
      shadowOpacity: 0.05,
      shadowRadius: 8,
      shadowOffset: { width: 0, height: 2 },
    },
    cardTop: { flexDirection: 'row', alignItems: 'center' },
    cardIcon: {
      width: 40,
      height: 40,
      borderRadius: 12,
      backgroundColor: colors.primaryLight,
      justifyContent: 'center',
      alignItems: 'center',
      marginRight: 12,
    },
    cardInfo: { flex: 1, marginRight: 8 },
    cardTitle: { fontSize: 16, fontWeight: '700', color: colors.textPrimary },
    cardMeta: { fontSize: 13, color: colors.textSecondary, marginTop: 2 },
    cardStats: {
      flexDirection: 'row',
      marginTop: 14,
      paddingTop: 12,
      borderTopWidth: 1,
      borderTopColor: colors.divider,
    },
    statBlock: { flex: 1 },
    statLabel: { fontSize: 11, fontWeight: '700', color: colors.textMuted, textTransform: 'uppercase', marginBottom: 4 },
    statValue: { fontSize: 16, fontWeight: '800', color: colors.textPrimary },
    statValueSmall: { fontSize: 13, fontWeight: '600', color: colors.textPrimary, marginTop: 2 },
    gradeBadge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
    gradeBadgeText: { fontSize: 13, fontWeight: '800' },
    downloadButton: {
      marginTop: 14,
      backgroundColor: isDark ? colors.primary : '#17253C',
      borderRadius: 8,
      paddingVertical: 10,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    downloadButtonText: { color: '#FFFFFF', fontSize: 14, fontWeight: '600' },
  });
